/* eslint-disable @typescript-eslint/no-explicit-any */
import { getRefreshToken } from '@vgrubs/components';
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.NX_API_URL;

export type MenuImportAiFinishedPayload = {
  brandId: number;
  jobId: string;
  status: string;
};

/**
 * Socket for the "Import Menu with AI" flow. Connects to the same `/menu`
 * namespace as the scraping socket and listens for `menu_import_ai_finished`,
 * which the worker emits (with the `jobId`) when parsing is done. Subscribe to
 * a store's room via the returned `observeMenu` before/while a job runs.
 */
const useMenuImportAiSocket = ({
  onFinished,
}: {
  onFinished: (payload: MenuImportAiFinishedPayload) => void;
}) => {
  const socketRef = useRef<any>(null);
  const refreshToken = getRefreshToken();

  // Keep the latest callback without reconnecting the socket.
  const onFinishedRef = useRef(onFinished);
  useEffect(() => {
    onFinishedRef.current = onFinished;
  }, [onFinished]);

  useEffect(() => {
    if (!refreshToken) return;

    const socket = io(SOCKET_URL, {
      path: '/menu',
      transports: ['websocket'],
      auth: {
        authorization: refreshToken, // Token in handshake
      },
    });

    socketRef.current = socket;

    // Worker finished parsing the uploaded document.
    socket.on('menu_import_ai_finished', (data: MenuImportAiFinishedPayload) => {
      onFinishedRef.current(data);
    });

    socket.on('connect_error', (err) => {
      console.error('Connection Error:', err.message);
    });

    // Clean up on unmount
    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const observeMenu = (
    locationId: string | number,
    brandId: string | number,
    subscribe: boolean
  ) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('observe-menu', {
        locationId,
        brandId,
        subscribe,
      });
    }
  };

  return { observeMenu };
};

export default useMenuImportAiSocket;
